import { Controller } from 'stimulus'

// Toggles the account-preferences page from its read-only state into edit mode
// entirely client-side — no server round-trip and no innerHTML swap — so the
// scroll position stays exactly where the user was reading.
//
// Plain form controls are covered by a disabled <fieldset>. Quill rich-text
// editors need separate handling: they ignore the fieldset (their editable area
// is a contenteditable div, not a form control) and cannot be re-rendered under
// React 18 without a `removeChild` crash. So instead of remounting we toggle each
// Quill instance's own enabled state through its API. The editors are mounted
// editable (so their text-change -> hidden <textarea> sync is wired up) and we
// disable them for the initial view state as they finish mounting.
export default class extends Controller {
  static targets = ['fieldset']

  connect() {
    this.editing = false
    this._trackHeaderHeight()
    this._setQuillsEnabled(false)
    // react_ujs mounts the Quill editors asynchronously, so disable each one as
    // it appears while we are still in view mode.
    this._observer = new MutationObserver(() => {
      if (!this.editing) this._setQuillsEnabled(false)
    })
    this._observer.observe(this.element, { childList: true, subtree: true })

    // The Quill component leaves its hidden <textarea> blank until the user types,
    // so an untouched rich-text field would submit blank and wipe its stored value.
    // Copy each editor's content into its textarea right before the form is sent
    // (capture phase, so it runs before Rails-UJS serializes the remote form).
    this._form = this.element.querySelector('form')
    if (this._form) {
      this._onSubmit = () => this._syncQuillTextareas()
      this._form.addEventListener('submit', this._onSubmit, true)
    }
  }

  // Tells the scroller how much of its top the sticky header covers, so a section
  // jumped to does not land its title under it.
  _trackHeaderHeight() {
    const header   = this.element.querySelector('.ap-header')
    const scroller = this.element.closest('.workorder-form.preferences') || this.element
    if (!header) return

    const publish = () => scroller.style.setProperty('--ap-header-height', `${Math.round(header.offsetHeight)}px`)
    publish()

    if (typeof ResizeObserver === 'undefined') return
    this._headerObserver = new ResizeObserver(publish)
    this._headerObserver.observe(header)
  }

  disconnect() {
    if (this._headerObserver) this._headerObserver.disconnect()
    if (this._observer) this._observer.disconnect()
    if (this._form && this._onSubmit) {
      this._form.removeEventListener('submit', this._onSubmit, true)
    }
  }

  edit() {
    this.editing = true
    this.element.classList.add('editing')
    if (this.hasFieldsetTarget) this.fieldsetTarget.disabled = false
    if (this._observer) this._observer.disconnect()
    this._setQuillsEnabled(true)
  }

  _setQuillsEnabled(enabled) {
    if (!window.Quill) return
    this.element.querySelectorAll('.ql-container').forEach((container) => {
      const quill = window.Quill.find(container)
      if (quill && quill.isEnabled() !== enabled) quill.enable(enabled)
    })
  }

  _syncQuillTextareas() {
    if (!window.Quill) return
    this.element.querySelectorAll('.ql-container').forEach((container) => {
      const quill = window.Quill.find(container)
      if (!quill) return
      const wrapper = container.closest('[data-react-class]')
      const textarea = wrapper && wrapper.querySelector('textarea')
      // Only carry over editors that actually have content — leaving genuinely
      // empty fields blank rather than storing "<p><br></p>" (which reads as
      // present downstream). getLength() === 1 is Quill's empty state.
      if (textarea && quill.getLength() > 1) {
        textarea.value = quill.getSemanticHTML().replaceAll('<p></p>', '<p><br></p>')
      }
    })
  }
}
